import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild, ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import mermaid, {MermaidConfig} from 'mermaid';
import {BackendService} from '../../../services/general/backend.service';
import {SurveyService} from '../../../services/surveys.service';
import {Router} from '@angular/router';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {TranslateService} from '@ngx-translate/core';
import {lowerCase} from 'lodash';

import {MatDialog} from '@angular/material/dialog';
import {ModalSurveyRevisionPreviewComponent} from '../../../modals/modal-survey-revision-preview/modal-survey-revision-preview.component';
import {ModalSplitQuestionComponent} from '../../../modals/modal-split-question/modal-split-question.component';
import {ModalReviseQuestionComponent} from '../../../modals/modal-revise-question/modal-revise-question.component';
import {UserService} from '../../../services/user.service';
import {StatementGeneratorService} from '../../../services/statement-generator.service';
import {ToastService} from '../../../services/general/toast.service';

@Component({
    selector: 'app-survey-dashboard',
    templateUrl: './survey-dashboard.component.html',
    styleUrls: ['./survey-dashboard.component.scss'],
    standalone: false
})
export class SurveyDashboardComponent implements OnInit {

  constructor(
    protected surveyService: SurveyService,
    private router: Router,
    private translateService: TranslateService,
    private dialog: MatDialog,
    public userService: UserService,
    private statementGeneratorService: StatementGeneratorService,
    private toast: ToastService
  ) {
  }

  @Input() survey: any;

  surveyDashboard: { [key: string]: DashboardItem } = {};
  loading = false;
  surveyCharts: { [key: string]: any } = {};
  previewLoading = false;

  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;

  filters = {
    certainty: [0, 100],
    contradiction: [0, 100],
    agree: [0, 100],
    disagree: [0, 100],
    section: '',
    preliminary_round_assessment: [],
    search: '',
    decision: 'all' as DecisionFilter,
    outcome: null as Advice | null,
  };

  readonly decisionFilterOptions: DecisionFilter[] = ['all', 'pending', 'decided'];

  /** Stato paginazione per ogni tipologia (key) */
  paginationState: { [key: string]: { pageIndex: number; pageSize: number } } = {};

  /** Grafico e filtri avanzati nascosti per default. */
  filtersExpanded: { [key: string]: boolean } = {};

  toggleFilters(key: string): void {
    this.filtersExpanded[key] = !this.filtersExpanded[key];
  }

  protected readonly objectKeys = Object.keys;

  /**
   * Confronto inline originale/rivisto dentro la card, senza aprire il
   * modal. revision_question/split_questions vivono su this.survey.questions
   * (l'oggetto "element" della dashboard e' un DTO piu' minimale restituito
   * da getSurveyDashboard(), senza customs) - stessa fonte gia' letta da
   * openRevisionModal()/setFinalDecision().
   *
   * revision_question e split_questions possono coesistere sulla stessa
   * domanda (nessuno dei due viene mai ripulito quando si cambia decisione),
   * quindi qual mostrare - e se mostrare qualcosa - dipende dalla decisione
   * ATTUALE (element.final_decision), non dalla semplice presenza del campo.
   */
  private findRevisionSourceQuestion(element: any): any {
    return this.survey?.questions?.find((q: any) => q.id === element.id);
  }

  hasRevisionComparison(element: any): boolean {
    return element.final_decision === 'revised' || element.final_decision === 'revised_split';
  }

  getRevisedStatement(element: any): string | null {
    if (element.final_decision !== 'revised') return null;
    return this.findRevisionSourceQuestion(element)?.customs?.revision_question?.question ?? null;
  }

  getSplitStatements(element: any): any[] {
    if (element.final_decision !== 'revised_split') return [];
    return this.findRevisionSourceQuestion(element)?.customs?.split_questions ?? [];
  }

  /**
   * Normalizza le metriche di ciascuna tipologia (CONSENSUS calcolato qui
   * stesso, le altre lette da element.metrics calcolato lato backend in
   * dashboard()) in una lista di blocchi omogenei, cosi' il template ha un
   * solo renderer generico (dual-bar/stat/bar-list/stat-list) invece di un
   * @if per tipologia.
   */
  algorithmicAdvice(element: any): Advice {
    return element.advice ?? 'pending';
  }

  adviceIcon(advice: Advice): string {
    return advice === 'accept' ? 'check_circle' : advice === 'reject' ? 'cancel' : advice === 'revise' ? 'edit_note' : 'hourglass_empty';
  }

  decisionColor(decisionId: string): string {
    return this.surveyService.questionFinalDecisionStatuses.find((status: any) => status.id === decisionId)?.bgcolor ?? PENDING_TONE;
  }

  adviceTone(element: any): string {
    const decision = element.recommended_decisions?.[0];
    return decision ? this.decisionColor(decision) : PENDING_TONE;
  }

  summaryTone(advice: Advice): string {
    return advice === 'accept' ? this.decisionColor('accepted') : advice === 'reject' ? this.decisionColor('rejected') : advice === 'revise' ? this.decisionColor('revised') : PENDING_TONE;
  }

  consensusStatusKey(element: any): string {
    return 'components.surveys.survey-dashboard.survey-dashboard.component.consensus-status.' + String(element.status || 'UNAVAILABLE').toLowerCase();
  }

  paraconsistentMetric(element: any, metric: string): number | null {
    const value = element.paraconsistent?.metrics?.[metric];
    return typeof value === 'number' ? value : null;
  }

  recommendedDecisions(key: string, element: any): string[] {
    if (key !== 'CONSENSUS') {
      return [];
    }
    return element.recommended_decisions ?? [];
  }

  decisionStatus(element: any): any {
    return this.surveyService.questionFinalDecisionStatuses.find((status: any) => status.id === element.final_decision) ?? null;
  }

  questionNumber(key: string, element: any): number {
    const data = this.surveyDashboard[key]?.questions?.data || [];
    return data.indexOf(element) + 1;
  }

  get decisionSegments(): { id: string; label: string; color: string; count: number; pct: number }[] {
    const { total } = this.decisionProgress;
    if (!total) {
      return [];
    }
    const counts: { [id: string]: number } = {};
    this.objectKeys(this.surveyDashboard).forEach((key) => {
      (this.surveyDashboard[key]?.questions?.data || []).forEach((q: any) => {
        if (q.final_decision) {
          counts[q.final_decision] = (counts[q.final_decision] || 0) + 1;
        }
      });
    });
    return this.surveyService.questionFinalDecisionStatuses
      .filter((status: any) => counts[status.id])
      .map((status: any) => ({
        id: status.id,
        label: this.survey?.is_revised ? status.title : status.titleNoRev,
        color: status.bgcolor,
        count: counts[status.id],
        pct: (counts[status.id] / total) * 100,
      }));
  }

  get advancedFiltersActive(): number {
    const ranges = ['certainty', 'contradiction', 'agree', 'disagree'];
    let active = ranges.filter((range) => this.filters[range][0] > 0 || this.filters[range][1] < 100).length;
    if (this.filters.section) {
      active++;
    }
    if (this.filters.preliminary_round_assessment?.length) {
      active++;
    }
    return active;
  }

  get anyFilterActive(): boolean {
    return this.advancedFiltersActive > 0 || !!this.filters.search || this.filters.decision !== 'all' || !!this.filters.outcome;
  }

  toggleOutcomeFilter(outcome: Advice): void {
    this.filters.outcome = this.filters.outcome === outcome ? null : outcome;
    this.applyFilters();
  }

  setDecisionFilter(decision: DecisionFilter): void {
    this.filters.decision = decision;
    this.applyFilters();
  }

  updateSearch(value: string): void {
    this.filters.search = value || '';
    this.applyFilters();
  }

  resetFilters(): void {
    this.filters = {
      certainty: [0, 100],
      contradiction: [0, 100],
      agree: [0, 100],
      disagree: [0, 100],
      section: '',
      preliminary_round_assessment: [],
      search: '',
      decision: 'all',
      outcome: null,
    };
    this.applyFilters();
  }

  private applyFilters(): void {
    const serialized = JSON.stringify(this.filters);
    this.objectKeys(this.surveyDashboard).forEach((key) => {
      this.surveyDashboard[key].questions.filter = serialized;
      if (this.paginationState[key]) {
        this.paginationState[key].pageIndex = 0;
      }
    });
  }

  getMetricBlocks(key: string, element: any): MetricBlock[] {
    if (key === 'CONSENSUS') {
      return [
        {
          kind: 'dual-bar',
          label: this.translateService.instant('generic.votes'),
          leftPct: element.agree_perc,
          leftLabel: `${this.translateService.instant('generic.agree')} ${this.round(element.agree_perc, 0)}%`,
          rightPct: element.disagree_perc,
          rightLabel: `${this.translateService.instant('generic.disagree')} ${this.round(element.disagree_perc, 0)}%`,
        },
        {
          kind: 'stat',
          label: this.translateService.instant('generic.certainty-degree'),
          value: `${this.round(element.certainty_degree, 2)}%`,
        },
        {
          kind: 'stat',
          label: this.translateService.instant('generic.contradiction-degree'),
          value: `${this.round(element.contradiction_degree, 2)}%`,
        },
      ];
    }

    const m = element.metrics;
    if (!m) return [];

    switch (m.type) {
      case 'choices':
        return [
          {
            kind: 'bar-list',
            label: this.translateService.instant('generic.answer-distribution'),
            bars: (m.options || []).map((o: any) => ({ label: o.label, pct: o.perc, valueLabel: `${o.perc}%` })),
          },
          {
            kind: 'stat',
            label: this.translateService.instant('generic.responses'),
            value: `${m.respondents}`,
            caption: this.translateService.instant('generic.respondents'),
          },
        ];

      case 'grid':
        return (m.rows || []).map((row: any) => ({
          kind: 'bar-list' as const,
          label: row.title,
          bars: (m.columns || []).map((col: any) => {
            const count = row.counts?.[col.id] ?? 0;
            const pct = row.total > 0 ? Math.round((count / row.total) * 100) : 0;
            return { label: col.title, pct, valueLabel: `${count} (${pct}%)` };
          }),
        }));

      case 'classification':
        return (m.rows || []).map((row: any) => ({
          kind: 'stat-list' as const,
          label: row.title,
          items: (m.columns || []).map((col: any) => ({
            label: col.title,
            value: row.averages?.[col.id] != null ? `${row.averages[col.id]}` : 'N/A',
          })),
        }));

      case 'quantitative':
        return [
          { kind: 'stat', label: this.translateService.instant('generic.average'), value: m.mean != null ? `${m.mean}` : 'N/A' },
          { kind: 'stat', label: this.translateService.instant('generic.median'), value: m.median != null ? `${m.median}` : 'N/A' },
          { kind: 'stat', label: this.translateService.instant('generic.range'), value: m.min != null && m.max != null ? `${m.min} – ${m.max}` : 'N/A' },
        ];

      case 'weighted':
      case 'ranking': {
        const items = m.items || [];
        const maxAvg = Math.max(0, ...items.map((i: any) => i.avg ?? 0));
        return [
          {
            kind: 'bar-list',
            label: this.translateService.instant(m.type === 'weighted' ? 'generic.weighted-average' : 'generic.average-position'),
            bars: items.map((i: any) => ({
              label: i.label,
              pct: maxAvg > 0 && i.avg != null ? Math.round((i.avg / maxAvg) * 100) : 0,
              valueLabel: i.avg != null ? `${i.avg}` : 'N/A',
            })),
          },
        ];
      }

      case 'opened':
        return [
          { kind: 'stat', label: this.translateService.instant('generic.responses'), value: `${m.respondents}` },
        ];

      default:
        return [];
    }
  }

  private round(value: number, decimals: number): string {
    if (value == null || isNaN(value)) return '0';
    return value.toFixed(decimals);
  }

  get currentUserInTeam() {
    return this.survey?.members?.find(
      (member: any) => member?.user_id == this.userService.userDetails?.user_id,
    );
  }

  /**
   * Contatore "X / Y decisi", calcolato lato client sui dati gia' caricati
   * da getSurveyDashboard() su tutte le tipologie presenti (non solo
   * CONSENSUS - il selettore di decisione compare per ogni tipologia).
   */
  get decisionProgress(): { decided: number; total: number } {
    let decided = 0;
    let total = 0;
    this.objectKeys(this.surveyDashboard).forEach((key) => {
      const data = this.surveyDashboard[key]?.questions?.data || [];
      total += data.length;
      decided += data.filter((q: any) => !!q.final_decision).length;
    });
    return { decided, total };
  }

  get decisionProgressPercent(): number {
    const { decided, total } = this.decisionProgress;
    return total ? Math.round((decided / total) * 100) : 0;
  }

  /**
   * Tre pillole di riepilogo (Accettabile/Rigettabile/Da revisionare)
   * accanto alla barra di avanzamento - stessi totali gia' calcolati dal
   * backend per il grafico a torta, nessuna nuova chiamata. Stesse chiavi
   * i18n gia' in uso per il badge di ogni singola card.
   */
  get consensusSummary(): { advice: Advice; labelKey: string; count: number; perc: number }[] {
    const totals: ConsensusTotals = this.surveyDashboard['CONSENSUS']?.totals;
    if (!totals) return [];
    const toRevision = Object.values(totals.TO_REVISION || {});
    const prefix = 'components.surveys.survey-dashboard.survey-dashboard.component.';
    return [
      { advice: 'accept', labelKey: prefix + 'accepted', count: totals.ACCEPTED.count, perc: totals.ACCEPTED.perc },
      { advice: 'revise', labelKey: prefix + 'to_revision', count: toRevision.reduce((sum, t) => sum + t.count, 0), perc: Math.round(toRevision.reduce((sum, t) => sum + t.perc, 0) * 100) / 100 },
      { advice: 'reject', labelKey: prefix + 'rejected', count: totals.REJECTED.count, perc: totals.REJECTED.perc },
      ...(totals.PENDING?.count ? [{ advice: 'pending' as Advice, labelKey: prefix + 'rm.advice-pending', count: totals.PENDING.count, perc: totals.PENDING.perc }] : []),
    ];
  }

  ngOnInit(): void {
    this.loading = true;
    this.surveyService.getSurveyDashboard(
      this.survey?.id,
      (response) => {
        Object.keys(response).forEach((key) => {
          response[key].questions = new MatTableDataSource(response[key].questions || []);

          response[key].questions.filterPredicate = (q: any, filter: string) => {
            const filters = JSON.parse(filter);

            const certaintyOk =
              q.certainty_degree >= filters.certainty[0] &&
              q.certainty_degree <= filters.certainty[1];

            const agreeOk =
              q.agree_perc >= filters.agree[0] &&
              q.agree_perc <= filters.agree[1];

            const disagreeOk =
              q.disagree_perc >= filters.disagree[0] &&
              q.disagree_perc <= filters.disagree[1];

            const contradictionOk =
              q.contradiction_degree >= filters.contradiction[0] &&
              q.contradiction_degree <= filters.contradiction[1];

            const sectionOk =
              !filters.section || q.section === filters.section;

            const statusOk =
              filters.preliminary_round_assessment?.length === 0 ||
              filters.preliminary_round_assessment?.some((st) => st == q.status);

            const search = (filters.search || '').trim().toLowerCase();
            const searchOk = !search || [q.statement, q.question, q.section]
              .some((text: any) => (text || '').toString().toLowerCase().includes(search));

            const decisionOk = filters.decision === 'all'
              || (filters.decision === 'decided' ? !!q.final_decision : !q.final_decision);

            const outcomeOk = !filters.outcome || key !== 'CONSENSUS' || this.algorithmicAdvice(q) === filters.outcome;

            if (key !== 'CONSENSUS') {
              return sectionOk && searchOk && decisionOk;
            }

            return certaintyOk && contradictionOk && sectionOk && statusOk && disagreeOk && agreeOk && searchOk && decisionOk && outcomeOk;
          };

          if (key === 'CONSENSUS') {
            this.surveyCharts[key] = this.getConsensusPieChartOption(response[key].totals);
          }
        });

        this.surveyDashboard = response;
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  getConsensusPieChartOption(totals: ConsensusTotals): any {

    const toRevisionCount = Object.values(totals.TO_REVISION)
      .reduce((sum, t) => sum + t.count, 0);

    return {
      legend: {
        top: '0%',
        left: 'center',
        data: [
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.accepted'),
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.rejected'),
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision')
        ]
      },
      tooltip: {
        trigger: 'item',
        extraCssText: 'width: 500px; max-width: 800px; font-size: 14px;',
        formatter: (params: any) => {
          if (params.name !== this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision')) {
            return `${params.seriesName}<br/>${params.name} : ${params.value} (${params.percent}%)`;
          }

          const rows = Object.entries(totals.TO_REVISION)
            .map(([type, obj]) => `
        <div class="d-flex justify-content-between py-1">
          <div style="width: 33%;">
            ${this.translateService.instant(
              'components.surveys.survey-dashboard.survey-dashboard.component.' + type.toLowerCase()
            )}
          </div>
          <div class="text-center" style="width: 33%;">${obj.count}</div>
          <div class="text-end" style="width: 33%;">${obj.perc}%</div>
        </div>
      `).join('');

          return `
      <div style="min-width: 500px;">
        <div class="d-flex justify-content-between py-1 fw-bold">
          <div style="width: 33%;">${this.translateService.instant('generic.type')}</div>
          <div class="text-center" style="width: 33%;">${this.translateService.instant('generic.number')}</div>
          <div class="text-end" style="width: 33%;">%</div>
        </div>

        ${rows}
      </div>
    `;
        }
      },
      series: [
        {
          name: 'Consensus',
          type: 'pie',
          radius: '50%',
          center: ['50%', '50%'],
          selectedMode: 'single',
          data: [
            {
              value: totals.ACCEPTED.count,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.accepted'),
              itemStyle: {color: this.summaryTone('accept')}
            },
            {
              value: totals.REJECTED.count,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.rejected'),
              itemStyle: {color: this.summaryTone('reject')}
            },
            {
              value: toRevisionCount,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision'),
              itemStyle: {color: this.summaryTone('revise')}
            }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  }

  formatPercent(value: number): string {
    return value + '%';
  }

  updateFilter(key: string, type: string, value: number, index: number) {
    this.filters[type][index] = value;
    this.applyFilters();
  }

  updateSectionFilter(key: string, value: string) {
    this.filters.section = value;
    this.applyFilters();
  }

  updateStatusFilter(key: string, status: []) {
    this.filters.preliminary_round_assessment = status;
    this.applyFilters();
  }

  keyQuestionsSections(key): string[] {
    const data = this.surveyDashboard[key]?.questions?.data || [];
    const sections = data.map((q: any) => q.section).filter((s: any) => !!s);
    return Array.from(new Set(sections));
  }

  keyQuestionsStatuses(key): string[] {
    const data = this.surveyDashboard[key]?.questions?.data || [];
    const sections = data.map((q: any) => q.status).filter((s: any) => !!s);
    return Array.from(new Set(sections));
  }

  /** Dati filtrati per le card (stesso risultato dei filtri sulla tabella) */
  getFilteredQuestions(key: string): any[] {
    const ds = this.surveyDashboard[key]?.questions;
    if (!ds) return [];
    return (ds as any).filteredData ?? ds.data ?? [];
  }

  /** Dati paginati per le card */
  getPaginatedQuestions(key: string): any[] {
    const filtered = this.getFilteredQuestions(key);
    const { pageIndex, pageSize } = this.getPaginationState(key);
    const start = pageIndex * pageSize;
    return filtered.slice(start, start + pageSize);
  }

  typologyTitleKey(key: string): string {
    return `service.survey-service.question-typologies.${key.toLowerCase().replace(/_/g, '-')}.title`;
  }

  hasSections(key: string): boolean {
    return this.keyQuestionsSections(key).length > 0;
  }

  /** Domande filtrate raggruppate per sezione (nell'ordine di prima comparsa), quelle senza sezione in fondo. */
  private getQuestionsGroupedBySection(key: string): any[] {
    const filtered = this.getFilteredQuestions(key);
    if (!this.hasSections(key)) {
      return filtered;
    }

    const groups = new Map<string, any[]>();
    filtered.forEach((q: any) => {
      const name = q.section || '';
      if (!groups.has(name)) {
        groups.set(name, []);
      }
      groups.get(name).push(q);
    });

    const withoutSection = groups.get('') || [];
    groups.delete('');
    return [...Array.from(groups.values()).flat(), ...withoutSection];
  }

  /** Card della pagina corrente divise per sezione, con l'avanzamento delle decisioni dell'intera sezione. */
  getPaginatedGroups(key: string): { name: string; items: any[]; decided: number; total: number }[] {
    const ordered = this.getQuestionsGroupedBySection(key);
    const { pageIndex, pageSize } = this.getPaginationState(key);
    const page = ordered.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);

    const progress = new Map<string, { decided: number; total: number }>();
    ordered.forEach((q: any) => {
      const name = q.section || '';
      const entry = progress.get(name) || { decided: 0, total: 0 };
      entry.total++;
      if (q.final_decision) {
        entry.decided++;
      }
      progress.set(name, entry);
    });

    const groups: { name: string; items: any[]; decided: number; total: number }[] = [];
    page.forEach((q: any) => {
      const name = q.section || '';
      const last = groups[groups.length - 1];
      if (last && last.name === name) {
        last.items.push(q);
      } else {
        groups.push({ name, items: [q], ...progress.get(name) });
      }
    });
    return groups;
  }

  getPaginationState(key: string): { pageIndex: number; pageSize: number } {
    if (!this.paginationState[key]) {
      this.paginationState[key] = { pageIndex: 0, pageSize: 10 };
    }
    const filteredLen = this.getFilteredQuestions(key).length;
    const maxPage = Math.max(0, Math.ceil(filteredLen / this.paginationState[key].pageSize) - 1);
    if (this.paginationState[key].pageIndex > maxPage) {
      this.paginationState[key].pageIndex = maxPage;
    }
    return this.paginationState[key];
  }

  onPageChange(key: string, event: PageEvent): void {
    this.paginationState[key] = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    };
  }

  previewSurveyRevision() {
    this.previewLoading = true;
    this.surveyService.getSurveyRevisionPreview(this.survey.id)
      .subscribe({
        next: (response) => {
          response.survey_is_closed = this.survey.status === 'CLOSED';
          this.dialog.open(ModalSurveyRevisionPreviewComponent, {
            data: response,
            panelClass: 'medium-dialog',
          });
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.previewLoading = false;
        }
      });
  }

  setFinalDecision(question: any, decision: any) {
    if (decision === 'revised_split' || decision === 'revised') {
      // Se la domanda ha gia' una revisione/split salvati per questa
      // specifica decisione, riapri l'editor direttamente invece di
      // rilanciare la pipeline AI.
      const existingQuestion = this.survey.questions.find(
        (q: any) => q.id === question.id,
      );
      const hasExistingRevisionData =
        decision === 'revised_split'
          ? !!existingQuestion?.customs?.split_questions
          : !!existingQuestion?.customs?.revision_question;
      if (hasExistingRevisionData) {
        this.openRevisionModal(question, decision, null, []);
        return;
      }

      // TEMPORANEO: pipeline di riscrittura AI troppo lenta, salta
      // direttamente all'editor - senza rewritten_statement/statements, i
      // modali pre-riempiono da soli con l'originale
      // (ModalReviseQuestionComponent) o con righe vuote da scrivere a mano
      // (ModalSplitQuestionComponent). Rimuovere questo blocco (e il
      // "return" sotto) per riattivare la pipeline quando i tempi di
      // generazione saranno accettabili.
      // const AI_STATEMENT_REWRITE_ENABLED = false;
      // if (!AI_STATEMENT_REWRITE_ENABLED) {
      //   this.openRevisionModal(question, decision, null, []);
      //   return;
      // }

      // Fire the AI statement-rewriting pipeline first, then open the
      // revise/split modal pre-filled with the rewritten statement.
      // Dashboard decision id -> pipeline directive.
      const pipelineDecision =
        decision === 'revised_split' ? 'SPLIT' : 'REVISE';

      question.rewriting = true;
      this.statementGeneratorService.rewriteStatement(
        question.id,
        this.survey.id,
        pipelineDecision,
        (result: { rewritten_statement: string; statements: string[] }) => {
          question.rewriting = false;
          const statements = result.statements || [];
          // The pipeline result decides the outcome: more than one statement
          // means the statement was split, regardless of the button clicked.
          const effectiveDecision =
            statements.length > 1 ? 'revised_split' : 'revised';
          this.openRevisionModal(
            question,
            effectiveDecision,
            result.rewritten_statement,
            statements,
          );
        },
        () => {
          question.rewriting = false;
          question.final_decision = null;
          this.toast.error(
            this.translateService.instant('generic.error'),
            this.translateService.instant(
              'components.survey-all-responses.rewrite-statement-error',
            ),
          );
        },
      );
      return;
    } else {
      if (decision == question.final_decision) {
        decision = null;
      }
    }

    this.updateDecisionWithData(question, decision);
  }

  /**
   * Open the revise/split modal for a question, pre-filled with the statement
   * produced by the rewriting pipeline, and persist the decision on close.
   */
  private openRevisionModal(question: any, decision: string, rewrittenStatement: string, statements: string[] = []) {
    const questionTmp = this.survey.questions.find((q: any) => q.id === question.id);

    if (decision === 'revised_split') {
      const dialogRef = this.dialog.open(ModalSplitQuestionComponent, {
        data: {question: questionTmp, rewritten_statement: rewrittenStatement, statements, survey: this.survey},
        panelClass: 'medium-dialog',
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result?.cancelled) {
            question.final_decision = null;
            this.updateDecisionWithData(question, null);
          } else {
            question.final_decision = result.decision;
            questionTmp.customs.split_questions = result;
            this.updateDecisionWithData(question, decision, {split_questions: result});
          }
        }
      });
    } else if (decision === 'revised') {
      const dialogRef = this.dialog.open(ModalReviseQuestionComponent, {
        // app-single-question (in modalita' editabile) richiede la survey
        // per rendere correttamente le impostazioni specifiche della
        // tipologia della domanda.
        data: {question: questionTmp, rewritten_statement: rewrittenStatement, survey: this.survey},
        panelClass: 'medium-dialog',
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result?.cancelled) {
            question.final_decision = null;
            this.updateDecisionWithData(question, null);
          } else {
            question.final_decision = result.decision;
            questionTmp.customs.revision_question = result;
            this.updateDecisionWithData(question, decision, {revision_question: result});
          }
        }
      });
    }
  }

  updateDecisionWithData(question: any, decision: any, extraData: any = {}) {
    const question_tmp = {...question};
    const survey_question = this.survey?.questions?.find((q: any) => q.id === question.id);

    question.final_decision = decision;
    survey_question.final_decision = decision;

    const model = {
      final_decision: question.final_decision,
      ...extraData
    };

    this.surveyService.updateQuestionFinalDecision(
      this.survey.id,
      question.id,
      model,
      () => {
      },
      (err) => {
        question.final_decision = question_tmp.final_decision;
        survey_question.final_decision = question_tmp.final_decision;
      }
    );
  }
}

type Advice = 'accept' | 'revise' | 'reject' | 'pending';

const PENDING_TONE = '#6b7280';

type DecisionFilter = 'all' | 'pending' | 'decided';

interface DashboardItem {
  questions: MatTableDataSource<any>;

  [key: string]: any;
}

interface ToRevisionType {
  [key: string]: { count: number; perc: number };
}

interface ConsensusTotals {
  ACCEPTED: { count: number; perc: number };
  REJECTED: { count: number; perc: number };
  TO_REVISION: ToRevisionType;
  PENDING?: { count: number; perc: number };
}

interface MetricBlock {
  kind: 'dual-bar' | 'stat' | 'bar-list' | 'stat-list';
  label: string;
  // dual-bar
  leftPct?: number;
  leftLabel?: string;
  rightPct?: number;
  rightLabel?: string;
  // stat
  value?: string;
  caption?: string;
  // bar-list
  bars?: { label: string; pct: number; valueLabel: string }[];
  // stat-list
  items?: { label: string; value: string }[];
}
